import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

export class SkincareProtocols extends UcoastEl {
	static htmlSelector = 'skincare-protocols'

	private protocolToggles: HTMLButtonElement[]
	private protocolPanels: HTMLElement[]
	private protocolsContainer?: HTMLElement
	private tipsToggles: HTMLButtonElement[]
	private protocolTransitionTimeout?: number

	constructor() {
		super()

		this.protocolToggles = Array.from(
			this.querySelectorAll<HTMLButtonElement>('[data-skincare-protocol-toggle]')
		)
		this.protocolPanels = Array.from(
			this.querySelectorAll<HTMLElement>('[data-skincare-protocol-panel]')
		)
		this.protocolsContainer = this.querySelector<HTMLElement>(
			'.skincare-protocols__protocols'
		) ?? undefined
		this.tipsToggles = Array.from(
			this.querySelectorAll<HTMLButtonElement>('[data-skincare-tips-toggle]')
		)
	}

	override connectedCallback() {
		super.connectedCallback()

		if (!this.protocolToggles.length || !this.protocolPanels.length) return

		this.protocolToggles.forEach((toggle) =>
			toggle.addEventListener('click', this.handleProtocolToggleClick)
		)
		this.tipsToggles.forEach((toggle) =>
			toggle.addEventListener('click', this.handleTipsToggleClick)
		)

		this.protocolPanels.forEach((panel) => {
			this.getStepTriggers(panel).forEach((trigger) =>
				trigger.addEventListener('click', this.handleStepTriggerClick)
			)
			this.getStepBodies(panel).forEach((body) => this.prepareStepBody(body))
		})

		const initialProtocolIndex = this.protocolToggles.findIndex((toggle) =>
			toggle.classList.contains('is-active')
		)

		this.activateProtocol(
			initialProtocolIndex >= 0 ? initialProtocolIndex : 0
		)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()

		this.protocolToggles.forEach((toggle) =>
			toggle.removeEventListener('click', this.handleProtocolToggleClick)
		)
		this.tipsToggles.forEach((toggle) =>
			toggle.removeEventListener('click', this.handleTipsToggleClick)
		)

		this.protocolPanels.forEach((panel) => {
			this.getStepTriggers(panel).forEach((trigger) =>
				trigger.removeEventListener('click', this.handleStepTriggerClick)
			)
		})
	}

	private handleProtocolToggleClick = (event: MouseEvent) => {
		const toggle = q.oct<MouseEvent, HTMLButtonElement>(event)
		if (!toggle) return
		if (toggle.classList.contains('is-active')) return

		const protocolIndex = Number(toggle.dataset.protocolIndex)
		if (Number.isNaN(protocolIndex)) return

		this.activateProtocol(protocolIndex)
	}

	private handleStepTriggerClick = (event: MouseEvent) => {
		const trigger = q.oct<MouseEvent, HTMLButtonElement>(event)
		if (!trigger) return
		if (trigger.classList.contains('is-active')) return

		const protocolPanel = trigger.closest<HTMLElement>(
			'[data-skincare-protocol-panel]'
		)
		if (!protocolPanel) return

		const protocolIndex = Number(protocolPanel.dataset.protocolIndex)
		const stepIndex = Number(trigger.dataset.stepIndex)

		if (Number.isNaN(protocolIndex) || Number.isNaN(stepIndex)) return

		this.activateStep(protocolIndex, stepIndex)
	}

	private handleTipsToggleClick = (event: MouseEvent) => {
		const toggle = q.oct<MouseEvent, HTMLButtonElement>(event)
		if (!toggle) return

		const tips = toggle.closest<HTMLElement>('.skincare-protocols__tips')
		const body = tips?.querySelector<HTMLElement>('[data-skincare-tips-body]')
		if (!tips || !body) return

		const isExpanded = toggle.getAttribute('aria-expanded') === 'true'
		toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true')
		tips.classList.toggle('is-collapsed', isExpanded)
		body.hidden = isExpanded
	}

	private activateProtocol(protocolIndex: number) {
		if (!this.protocolsContainer) {
			this.setActiveProtocol(protocolIndex)
			return
		}

		window.clearTimeout(this.protocolTransitionTimeout)
		this.protocolsContainer.classList.add('is-fading-out')
		this.protocolsContainer.classList.remove('is-fading-in')

		this.protocolTransitionTimeout = window.setTimeout(() => {
			this.setActiveProtocol(protocolIndex)
			this.protocolsContainer?.classList.remove('is-fading-out')
			this.protocolsContainer?.classList.add('is-fading-in')

			this.protocolTransitionTimeout = window.setTimeout(() => {
				this.protocolsContainer?.classList.remove('is-fading-in')
			}, 280)
		}, 180)
	}

	private setActiveProtocol(protocolIndex: number) {
		this.protocolToggles.forEach((toggle, toggleIndex) => {
			const isActive = toggleIndex === protocolIndex
			toggle.classList.toggle('is-active', isActive)
			toggle.setAttribute('aria-pressed', isActive ? 'true' : 'false')
		})

		this.protocolPanels.forEach((panel, panelIndex) => {
			const isActive = panelIndex === protocolIndex
			panel.classList.toggle('is-active', isActive)
			panel.hidden = !isActive
		})

		const activePanel = this.protocolPanels[protocolIndex]
		if (!activePanel) return

		const currentStepIndex = Number(activePanel.dataset.currentStepIndex)
		const defaultStepIndex = Number(activePanel.dataset.defaultStepIndex)
		const nextStepIndex = Number.isNaN(currentStepIndex)
			? defaultStepIndex || 0
			: currentStepIndex

		this.activateStep(protocolIndex, nextStepIndex)
	}

	private activateStep(protocolIndex: number, stepIndex: number) {
		const panel = this.protocolPanels[protocolIndex]
		if (!panel) return

		panel.dataset.currentStepIndex = String(stepIndex)

		this.getStepTriggers(panel).forEach((trigger) => {
			const triggerStepIndex = Number(trigger.dataset.stepIndex)
			const isActive = triggerStepIndex === stepIndex
			trigger.classList.toggle('is-active', isActive)
			trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false')
		})

		this.getStepBodies(panel).forEach((body) => {
			const bodyStepIndex = Number(body.dataset.stepIndex)
			const isActive = bodyStepIndex === stepIndex
			body.classList.toggle('is-active', isActive)
			this.setStepBodyState(body, isActive)
		})

		this.getMediaPanels(panel).forEach((media) => {
			const mediaStepIndex = Number(media.dataset.stepIndex)
			const isActive = mediaStepIndex === stepIndex
			media.classList.toggle('is-active', isActive)
			media.hidden = !isActive
		})

		this.getFeaturedCards(panel).forEach((card) => {
			const cardStepIndex = Number(card.dataset.stepIndex)
			const isActive = cardStepIndex === stepIndex
			card.classList.toggle('is-active', isActive)
			card.hidden = !isActive
		})
	}

	private getStepTriggers(panel: HTMLElement) {
		return Array.from(
			panel.querySelectorAll<HTMLButtonElement>('[data-skincare-step-trigger]')
		)
	}

	private getStepBodies(panel: HTMLElement) {
		return Array.from(
			panel.querySelectorAll<HTMLElement>('[data-skincare-step-body]')
		)
	}

	private prepareStepBody(body: HTMLElement) {
		this.clearStepBodyAnimation(body)
		body.style.overflow = 'hidden'
		if (body.hidden || !body.classList.contains('is-active')) {
			body.style.height = '0px'
			body.style.opacity = '0'
			return
		}

		body.hidden = false
		body.style.height = 'auto'
		body.style.opacity = '1'
	}

	private setStepBodyState(body: HTMLElement, isActive: boolean) {
		this.clearStepBodyAnimation(body)

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			body.hidden = !isActive
			body.style.height = isActive ? 'auto' : '0px'
			body.style.opacity = isActive ? '1' : '0'
			return
		}

		if (isActive) {
			this.expandStepBody(body)
			return
		}

		this.collapseStepBody(body)
	}

	private expandStepBody(body: HTMLElement) {
		body.hidden = false
		body.style.overflow = 'hidden'
		const startHeight = body.hidden ? 0 : body.offsetHeight
		body.style.height = `${startHeight}px`
		body.style.opacity = '0'

		const targetHeight = body.scrollHeight

		const frame = window.requestAnimationFrame(() => {
			body.style.height = `${targetHeight}px`
			body.style.opacity = '1'
		})
		body.dataset.animationFrame = String(frame)

		const onTransitionEnd = (event: TransitionEvent) => {
			if (event.propertyName !== 'height') return
			body.removeEventListener('transitionend', onTransitionEnd)
			if (!body.classList.contains('is-active')) return
			body.style.height = 'auto'
			delete body.dataset.animationState
		}

		body.addEventListener('transitionend', onTransitionEnd)
		body.dataset.animationState = 'expanding'
	}

	private collapseStepBody(body: HTMLElement) {
		if (body.hidden) return

		body.style.overflow = 'hidden'
		body.style.height = `${body.scrollHeight}px`
		body.style.opacity = '1'
		void body.offsetHeight

		const frame = window.requestAnimationFrame(() => {
			body.style.height = '0px'
			body.style.opacity = '0'
		})
		body.dataset.animationFrame = String(frame)

		const onTransitionEnd = (event: TransitionEvent) => {
			if (event.propertyName !== 'height') return
			body.removeEventListener('transitionend', onTransitionEnd)
			if (body.classList.contains('is-active')) return
			body.hidden = true
			delete body.dataset.animationState
		}

		body.addEventListener('transitionend', onTransitionEnd)
		body.dataset.animationState = 'collapsing'
	}

	private clearStepBodyAnimation(body: HTMLElement) {
		const animationFrame = Number(body.dataset.animationFrame)
		if (!Number.isNaN(animationFrame)) {
			window.cancelAnimationFrame(animationFrame)
		}
		delete body.dataset.animationFrame
	}

	private getMediaPanels(panel: HTMLElement) {
		return Array.from(
			panel.querySelectorAll<HTMLElement>('[data-skincare-step-media]')
		)
	}

	private getFeaturedCards(panel: HTMLElement) {
		return Array.from(
			panel.querySelectorAll<HTMLElement>('[data-skincare-step-product]')
		)
	}
}
